import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
    { href: "/#services", label: "Services" },
    { href: "/#work", label: "Case Studies" },
    { href: "/#about", label: "Studio" },
    { href: "/#pricing", label: "Pricing" },
    { href: "/#insights", label: "Blog" },
];

export default function DigtekNavbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="digtek-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-xl border-b border-digtek-border shadow-[0_4px_25px_rgba(23,1,44,.06)]"
                    : "bg-transparent"
            }`}
        >
            <nav className="max-w-[1320px] mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
                <a
                    href="/"
                    data-testid="brand-logo"
                    className="flex items-center group"
                >
                    <span className="font-display font-bold text-2xl tracking-tight text-digtek-header">
                        Aximo<span className="text-digtek-purple">.</span>
                    </span>
                </a>

                <ul className="hidden lg:flex items-center gap-9">
                    {links.map((l) => (
                        <li key={l.href}>
                            <a
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                href={l.href}
                                className="text-[15px] font-semibold text-digtek-header hover:text-digtek-purple transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-digtek-purple hover:after:w-full after:transition-all"
                            >
                                {l.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className="flex items-center gap-3">
                    <a
                        href="/portal/login"
                        data-testid="navbar-client-login"
                        className="hidden md:inline-flex items-center text-sm font-semibold text-digtek-header hover:text-digtek-purple transition-colors"
                    >
                        Client login
                    </a>
                    <a
                        href="/#contact"
                        data-testid="navbar-cta"
                        className="hidden md:inline-flex btn-digtek h-12 px-6 text-[12px]"
                    >
                        Get in touch
                        <ArrowUpRight className="w-4 h-4" />
                    </a>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-digtek-header text-digtek-header hover:bg-digtek-header hover:text-digtek-lime transition-colors"
                        aria-label="Toggle menu"
                    >
                        {open ? (
                            <X className="w-5 h-5" />
                        ) : (
                            <Menu className="w-5 h-5" />
                        )}
                    </button>
                </div>
            </nav>

            {/* Mobile sheet */}
            <div
                className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
                    open ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
                } bg-white border-b border-digtek-border`}
            >
                <ul className="px-6 py-6 space-y-4">
                    {links.map((l) => (
                        <li key={l.href}>
                            <a
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="block font-display font-bold text-2xl tracking-tight text-digtek-header hover:text-digtek-purple"
                            >
                                {l.label}
                            </a>
                        </li>
                    ))}
                    <li>
                        <a
                            href="/portal/login"
                            data-testid="mobile-nav-client-login"
                            onClick={() => setOpen(false)}
                            className="block font-display font-semibold text-xl text-digtek-purple"
                        >
                            Client login
                        </a>
                    </li>
                    <li className="pt-2">
                        <a
                            href="/#contact"
                            data-testid="mobile-nav-cta"
                            onClick={() => setOpen(false)}
                            className="btn-digtek h-12 px-6 text-[12px] w-full justify-center"
                        >
                            Get in touch <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </li>
                </ul>
            </div>
        </header>
    );
}
